import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { CONTENT_KEY } from '../../../entity-data/entity.model';
import { IMasterListItemBuilder } from '../../../entity-master/components/master-list-item/master-list-item-builder.interface';
import { INotifyOfDeleteOptions } from '../../../entity-master/components/master-list-item/master-list-item.component';
import {
  IMasterListItemAction,
  IMasterListItemData,
} from '../../../entity-master/components/master-list-item/master-list-item.interface';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { TRACKED_TIMES_MASTER_ROUTE } from '../../../main/main.data';
import { Highlight } from '../../../search-data/sub-models/highlight.model';
import { IRouter } from '../../../shared/interfaces/router.interface';
import { FriendlyDatePipe } from '../../../shared/pipes/friendly-date/friendly-date.pipe';
import { FriendlyDurationPipe } from '../../../shared/pipes/friendly-duration/friendly-duration.pipe';
import { ClientPreview } from '../../../tracked-time-data/models/client-preview.model';
import { TrackedTime } from '../../../tracked-time-data/models/tracked-time.model';
import { TrackedTimeDataDispatcher } from '../../../tracked-time-data/tracked-time-data.dispatcher';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackedTimeActionsEnum } from '../tracked-time-master.data';

@Injectable()
export class TrackedTimeListItemBuilder implements IMasterListItemBuilder {
  constructor(
    private _trackedTimeDataDispatcher: TrackedTimeDataDispatcher,
    private _translateService: TranslateService,
    private _friendlyDatePipe: FriendlyDatePipe,
    private _friendlyTimePipe: FriendlyDurationPipe,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  buildListItemData(
    trackedTime: TrackedTime,
    displayStatus: boolean,
    highlight?: Highlight
  ): List<IMasterListItemData> {
    const billingName = trackedTime.hasValidClient()
      ? trackedTime.billingName
      : this._translateService.instant('TRACKED_TIME.DELETED_CLIENT');
    const listItemData = [
      {
        customClass: 'tracked-time-client-name',
        values: [
          {
            value: billingName,
            highlights: highlight
              ? highlight.getSubstringForPath([
                  TrackedTime.KEYS.CLIENT_PREVIEW,
                  CONTENT_KEY,
                  ClientPreview.CONTENT_KEYS.BILLING_NAME,
                ])
              : null,
          },
        ],
        e2eId: 'tracked-time-list-item-client-name',
        e2eValue: billingName,
      },
      {
        customClass: 'tracked-time-start-date',
        values: [
          { value: this._friendlyDatePipe.transform(trackedTime.startDate) },
        ],
        e2eId: 'tracked-time-list-item-start-date',
      },
      {
        customClass: 'tracked-time-notes',
        values: [
          {
            value: trackedTime.notes,
            highlights: highlight
              ? highlight.getSubstringForPath([
                  CONTENT_KEY,
                  TrackedTime.CONTENT_KEYS.NOTES,
                ])
              : null,
          },
        ],
        e2eId: 'tracked-time-list-item-notes',
        e2eValue: trackedTime.notes,
      },
    ] as IMasterListItemData[];

    if (displayStatus) {
      const statusE2eId = 'tracked-time-list-item-status';
      const status = trackedTime.billedDocument
        ? {
            customClass: 'tracked-time-status billed',
            values: [
              { value: this._translateService.instant('TRACKED_TIME.BILLED') },
            ],
            e2eId: statusE2eId,
          }
        : {
            customClass: 'tracked-time-status',
            values: [{ value: '' }],
            e2eId: statusE2eId,
          };

      listItemData.push(status);
    }

    listItemData.push({
      customClass: 'tracked-time-duration',
      values: [
        { value: this._friendlyTimePipe.transform(trackedTime.duration) },
      ],
      e2eId: 'tracked-time-list-item-duration',
      e2eValue: trackedTime.duration,
    });

    return List(listItemData);
  }

  buildListItemActions(
    trackedTime: TrackedTime,
    trackingObject: ITrackingObject,
    trackingScreenName: TrackingScreenNamesEnum
  ): List<List<IMasterListItemAction>> {
    const actions: IMasterListItemAction[] = [
      {
        label: this._translateService.instant('ACTION.DELETE_TRACKED_TIME'),
        deleteAction: true,
        trackingObject,
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'delete_input',
          trackingScreenName
        ),
        e2eId: 'tracked-time-list-item-delete-action',
      },
    ];

    if (!trackedTime.billedDocument) {
      actions.unshift({
        id: TrackedTimeActionsEnum.bill_time,
        label: this._translateService.instant('ACTION.BILL_TIME'),
        featureNames: [Toggles.KEYS.INVOICES],
        trackingObject,
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'bill_time',
          trackingScreenName
        ),
        e2eId: 'tracked-time-list-item-bill-time-action',
      });
    }

    return List([List(actions)]);
  }

  buildListItemRoute(
    trackingAction: ITrackingAction,
    trackingObject: ITrackingObject,
    trackedTimeId: string,
    position: number
  ): IRouter {
    return {
      route: [TRACKED_TIMES_MASTER_ROUTE, trackedTimeId],
      disabled: !trackedTimeId,
      trackingObject,
      trackingAction,
      e2eId: `tracked-time-list-item-${position}`,
    };
  }

  buildNotifyOnDeleteOptions(trackedTime: TrackedTime): INotifyOfDeleteOptions {
    const label = this._translateService.instant(
      'TRACKED_TIME.DELETED_MESSAGE',
      {
        clientName: trackedTime.billingName,
      }
    );

    return {
      dispatcher: this._trackedTimeDataDispatcher,
      entity: trackedTime,
      context: {
        label,
      },
    };
  }
}
